import { useEffect, useState } from "react";
import { api, fmtINR, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const statusBadge = {
  paid: "bg-emerald-500/10 text-emerald-400",
  partial: "bg-amber-500/10 text-amber-400",
  pending: "bg-red-500/10 text-red-400",
};

export default function Ledger() {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});

  const load = () => api.get("/payouts").then(r => setList(r.data));
  useEffect(() => { load(); }, []);

  const totalPending = list.reduce((s, p) => s + Math.max(0, (p.amount || 0) - (p.paid_amount || 0)), 0);
  const totalPaid = list.reduce((s, p) => s + (p.paid_amount || 0), 0);

  const openEdit = (p) => { setForm({ ...p, paid_amount: p.paid_amount, status: p.status }); setEdit(p); };

  const save = async () => {
    const payload = { ...form, paid_amount: Number(form.paid_amount) || 0, amount: Number(form.amount) || 0 };
    if (payload.paid_amount >= payload.amount) payload.status = "paid";
    else if (payload.paid_amount > 0) payload.status = "partial";
    else payload.status = "pending";
    await api.put(`/payouts/${edit.id}`, payload);
    toast.success("Updated"); setEdit(null); load();
  };

  return (
    <>
      <PageHeader title="Freelancer Ledger" subtitle="Every payout, automatically tracked" />
      <div className="p-8 space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="hairline rounded-lg p-4 bg-[#0F0F0F]">
            <div className="text-[10px] uppercase tracking-widest text-white/40">Total Payouts</div>
            <div className="mono text-2xl font-bold mt-1">{list.length}</div>
          </div>
          <div className="hairline rounded-lg p-4 bg-[#0F0F0F]">
            <div className="text-[10px] uppercase tracking-widest text-white/40">Paid Out</div>
            <div className="mono text-2xl font-bold mt-1 text-emerald-400">{fmtINR(totalPaid)}</div>
          </div>
          <div className="hairline rounded-lg p-4 bg-[#0F0F0F]">
            <div className="text-[10px] uppercase tracking-widest text-white/40">Outstanding</div>
            <div className="mono text-2xl font-bold mt-1 text-amber-400">{fmtINR(totalPending)}</div>
          </div>
        </div>

        <div className="hairline rounded-lg bg-[#0F0F0F] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-white/40">
              <tr className="hairline-b">
                <th className="text-left px-4 py-3">Freelancer</th>
                <th className="text-left px-4 py-3">Project</th>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-right px-4 py-3">Paid</th>
                <th className="text-right px-4 py-3">Balance</th>
                <th className="text-left px-4 py-3">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {list.map((p, i) => (
                <tr key={p.id} className={i % 2 === 0 ? "" : "bg-white/[0.01]"}>
                  <td className="px-4 py-3 font-medium">{p.freelancer_name}</td>
                  <td className="px-4 py-3 text-white/60">{p.project_name || "—"}</td>
                  <td className="px-4 py-3 mono text-xs text-white/60">{fmtDate(p.date)}</td>
                  <td className="px-4 py-3 text-right mono">{fmtINR(p.amount)}</td>
                  <td className="px-4 py-3 text-right mono text-emerald-400">{fmtINR(p.paid_amount)}</td>
                  <td className="px-4 py-3 text-right mono text-amber-400">{fmtINR(Math.max(0, (p.amount||0)-(p.paid_amount||0)))}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-widest ${statusBadge[p.status]}`}>{p.status}</span></td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`edit-payout-${p.id}`} onClick={() => openEdit(p)} className="text-xs text-white/60 hover:text-white">Update</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-[#0F0F0F] border-white/10">
          <DialogHeader><DialogTitle>Update Payout</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm">{edit?.freelancer_name} · {edit?.project_name}</div>
            <div><label className="text-[10px] uppercase tracking-widest text-white/40">Amount Due</label><input type="number" value={form.amount || 0} onChange={e => setForm({...form, amount: e.target.value})} className="w-full mt-1 bg-transparent border border-white/10 rounded-md px-3 py-2 text-sm mono" /></div>
            <div><label className="text-[10px] uppercase tracking-widest text-white/40">Amount Paid</label><input type="number" value={form.paid_amount || 0} onChange={e => setForm({...form, paid_amount: e.target.value})} className="w-full mt-1 bg-transparent border border-white/10 rounded-md px-3 py-2 text-sm mono" /></div>
            <div><label className="text-[10px] uppercase tracking-widest text-white/40">Notes</label><input value={form.notes || ""} onChange={e => setForm({...form, notes: e.target.value})} className="w-full mt-1 bg-transparent border border-white/10 rounded-md px-3 py-2 text-sm" /></div>
          </div>
          <DialogFooter><button data-testid="save-payout-btn" onClick={save} className="bg-white text-black text-xs font-semibold px-4 py-2 rounded-md hover:bg-white/90">Save</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
